import { useLocalization } from "@fluent/react";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { Combobox } from "@lending/components/ComboBox/Combobox";
import styles from "./MonthYearSelect.module.css";
import type { Control, FieldValues, Path } from "react-hook-form";

interface MonthYear {
  month: number;
  year: number;
}

interface MonthYearSelectProps<T extends FieldValues> {
  control: Control<T, unknown>;
  monthName: Path<T>;
  yearName: Path<T>;
  maxDate?: MonthYear;
}

function MonthYearSelect<T extends FieldValues>({
  control,
  monthName,
  yearName,
  maxDate,
}: MonthYearSelectProps<T>) {
  const { l10n } = useLocalization();
  const selectedYear = useWatch({ control, name: yearName });
  const maxMonth = maxDate?.month;
  const maxYear = maxDate?.year;

  const allMonthOptions = useMemo(
    () => [
      { code: "01", name: l10n.getString("month-january", null, "January") },
      { code: "02", name: l10n.getString("month-february", null, "February") },
      { code: "03", name: l10n.getString("month-march", null, "March") },
      { code: "04", name: l10n.getString("month-april", null, "April") },
      { code: "05", name: l10n.getString("month-may", null, "May") },
      { code: "06", name: l10n.getString("month-june", null, "June") },
      { code: "07", name: l10n.getString("month-july", null, "July") },
      { code: "08", name: l10n.getString("month-august", null, "August") },
      { code: "09", name: l10n.getString("month-september", null, "September") },
      { code: "10", name: l10n.getString("month-october", null, "October") },
      { code: "11", name: l10n.getString("month-november", null, "November") },
      { code: "12", name: l10n.getString("month-december", null, "December") },
    ],
    [l10n]
  );

  const monthOptions = useMemo(() => {
    if (!maxMonth || !maxYear) {
      return allMonthOptions;
    }

    const selectedYearNumber = Number(selectedYear);

    if (selectedYearNumber !== maxYear) {
      return allMonthOptions;
    }

    return allMonthOptions.filter((monthOption) => Number(monthOption.code) <= maxMonth);
  }, [allMonthOptions, maxMonth, maxYear, selectedYear]);

  const yearOptions = useMemo(() => {
    const latestYear = maxYear ?? new Date().getFullYear();

    return Array.from({ length: 81 }, (_, index) => {
      const year = String(latestYear - index);
      return { code: year, name: year };
    });
  }, [maxYear]);

  return (
    <div className={styles.fieldRow}>
      <Combobox
        control={control}
        name={monthName}
        label={l10n.getString("month-year-select-month", null, "Month")}
        items={monthOptions}
        icon="calendar"
      />
      <Combobox
        control={control}
        name={yearName}
        label={l10n.getString("month-year-select-year", null, "Year")}
        items={yearOptions}
        icon="calendar"
      />
    </div>
  );
}

export { MonthYearSelect };
